import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api } from '../api'
import type { Challenge } from './srp'
import type { Me } from '../types'

export interface AuthState {
  me: Me | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  /**
   * Set when the password somebody typed was right and spent.
   *
   * Somebody given a temporary password gets this instead of a session. It is
   * held in memory for this visit only and never written down: reloading loses
   * it, and they sign in with the temporary password again, which still works
   * because they have not replaced it yet.
   */
  mustChoosePassword: Challenge | null
  /** Sets the password they chose, which signs them in. */
  choosePassword: (password: string) => Promise<void>
  /** Back to the sign in form without choosing one. */
  cancelPasswordChoice: () => void
  signOut: () => Promise<void>
  /** Re-reads the session so the approvals badge and role count stay current. */
  refresh: () => Promise<void>
  /**
   * Tells the session how many roles the library just reported.
   *
   * The count beside the company name and the count in the library heading are
   * one number shown twice, and they used to come from two places and disagree.
   * They now read the same index, which fixed the permanent gap, but each
   * function caches that index for ten seconds in its own process, so for a few
   * seconds after a write they could still differ by one, which is the reported
   * symptom in miniature.
   *
   * So on the screen where both are visible, the sidebar takes the number the
   * list just returned. One fetch, one number, no window.
   */
  noteRoleCount: (total: number) => void
  isAdmin: boolean
  /**
   * Sets up companies and the people in them. Not a bigger admin: `isAdmin`
   * stays false, so nothing gated on it opens for them.
   */
  isSuperAdmin: boolean
  /** Why signing in through Auth0 did not work, in plain words (STE-1012). */
  signInProblem?: string
}

export const Ctx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)
  const [challenge, setChallenge] = useState<Challenge | null>(null)

  useEffect(() => {
    let live = true
    api
      .me()
      .then((m) => live && setMe(m))
      .catch(() => live && setMe(null))
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const out = await api.login(email, password)
    if (out.kind === 'newPasswordNeeded') {
      setChallenge(out.challenge)
      return
    }
    setMe(out.me)
  }, [])

  const choosePassword = useCallback(
    async (password: string) => {
      if (!challenge) return
      const signedIn = await api.finishNewPassword(challenge, password)
      // Cleared only once it has worked. Clearing first would drop the one
      // thing that can finish the sign in, and a refused password would strand
      // them on a screen with nothing left to submit.
      setChallenge(null)
      setMe(signedIn)
    },
    [challenge],
  )

  const cancelPasswordChoice = useCallback(() => setChallenge(null), [])

  const signOut = useCallback(async () => {
    await api.logout()
    setMe(null)
    setChallenge(null)
  }, [])

  const refresh = useCallback(async () => {
    try {
      setMe(await api.me())
    } catch {
      setMe(null)
    }
  }, [])

  const noteRoleCount = useCallback((total: number) => {
    setMe((current) => (current && current.jdCount !== total ? { ...current, jdCount: total } : current))
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      me,
      loading,
      signIn,
      mustChoosePassword: challenge,
      choosePassword,
      cancelPasswordChoice,
      signOut,
      refresh,
      noteRoleCount,
      isAdmin: me?.role === 'admin',
      isSuperAdmin: me?.role === 'superadmin',
    }),
    [me, loading, signIn, challenge, choosePassword, cancelPasswordChoice, signOut, refresh, noteRoleCount],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
